import React from "react";
import { X, Plus, Save } from "lucide-react";
import Mo3tiButton from "../../layout/Mo3tiButton";
import ReusableDropdown from "../../../../../components/ReusableDropdown";

const AddAllowanceForm = ({
  t,
  theme,
  currentTheme,
  formStyles,
  showAddForm,
  setShowAddForm,
  newAllowance,
  setNewAllowance,
  handleAddAllowance,
  loading,
  language,
  editingAllowanceId,
}) => {
  const isEditing = !!editingAllowanceId;
  const isOvertime = newAllowance.type === "overtime";

  const Toggle = ({ checked, onChange, label }) => (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        cursor: "pointer",
        color: currentTheme.colors.textSecondary,
        fontSize: "14px",
        fontWeight: "600",
      }}
    >
      <span
        onClick={() => onChange(!checked)}
        style={{
          width: "44px",
          height: "24px",
          borderRadius: "999px",
          background: checked
            ? currentTheme.colors.success
            : currentTheme.colors.border,
          position: "relative",
          transition: "all 0.2s ease",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: "2px",
            left: checked ? "22px" : "2px",
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            background: "#fff",
            transition: "all 0.2s ease",
          }}
        />
      </span>
      {label}
    </label>
  );

  return (
    <>
      {/* Add Allowance Form */}
      <div style={formStyles.section}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h3
            style={{
              margin: 0,
              color: theme === "dark" ? "#ffffff" : "#1a1a1a",
              fontSize: "20px",
              fontWeight: "700",
            }}
          >
            {isEditing
              ? t("editAllowance") || "Edit Allowance"
              : t("addAllowance") || "Add Allowance"}
          </h3>
          <Mo3tiButton
            variant="secondary"
            icon={showAddForm ? X : Plus}
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm
              ? t("cancel")
              : isEditing
              ? t("editAllowance") || "Edit Allowance"
              : t("addAllowance") || "Add Allowance"}
          </Mo3tiButton>
        </div>

        {showAddForm && (
          <div>
            <div style={formStyles.formGrid}>
              {/* Type */}
              <div style={formStyles.formGroup}>
                <label style={formStyles.label}>
                  {t("allowanceType") || "Allowance Type"}
                </label>
                <ReusableDropdown
                  items={[
                    { id: "transportation", label: t("transportation") || "Transportation" },
                    { id: "housing", label: t("housing") || "Housing" },
                    { id: "meal", label: t("meal") || "Meal" },
                    { id: "variable", label: t("variable") || "Variable" },
                    { id: "overtime", label: t("overtime") || "Overtime" },
                    { id: "bonus", label: t("bonus") || "Bonus" },
                    { id: "commission", label: t("commission") || "Commission" },
                    { id: "representation", label: t("representation") || "Representation" },
                    { id: "other", label: t("other") },
                  ]}
                  value={newAllowance.type || ""}
                  onChange={(val) => {
                    if (val) setNewAllowance({ ...newAllowance, type: val });
                  }}
                  onSelect={(item) =>
                    setNewAllowance({ ...newAllowance, type: item.id })
                  }
                  placeholder={t("selectType")}
                  theme={theme}
                  language={language}
                  maxItems={9}
                  getItemKey={(item) => item.id}
                  getItemLabel={(item) => item.label}
                  showAllOnArrowClick={true}
                  showClear={true}
                  onClear={() =>
                    setNewAllowance({
                      ...newAllowance,
                      type: "",
                      amount: "",
                      description: "",
                    })
                  }
                  inputStyle={formStyles.input}
                />
              </div>

              {/* Amount */}
              <div style={formStyles.formGroup}>
                <label style={formStyles.label}>{t("amount")}</label>
                <input
                  type="number"
                  style={formStyles.input}
                  value={newAllowance.amount}
                  onChange={(e) =>
                    setNewAllowance({ ...newAllowance, amount: e.target.value })
                  }
                  placeholder="0.00"
                />
              </div>

              {/* Days OR Hours */}
              <div style={formStyles.formGroup}>
                <label style={formStyles.label}>
                  {isOvertime ? t("hours") || "Hours" : t("days")}
                </label>
                <input
                  type="number"
                  style={formStyles.input}
                  value={isOvertime ? newAllowance.hours : newAllowance.days}
                  onChange={(e) => {
                    if (isOvertime) {
                      setNewAllowance({
                        ...newAllowance,
                        hours: e.target.value,
                      });
                    } else {
                      setNewAllowance({
                        ...newAllowance,
                        days: e.target.value,
                      });
                    }
                  }}
                  placeholder="0"
                  step={isOvertime ? "0.5" : "1"}
                />
              </div>
            </div>

            {/* Optional percentage-based calculation */}
            <div style={formStyles.formGrid}>
              <div style={formStyles.formGroup}>
                <label style={formStyles.label}>
                  {t("percentage") || "Percentage %"}
                </label>
                <input
                  type="number"
                  style={formStyles.input}
                  value={newAllowance.percentage}
                  onChange={(e) =>
                    setNewAllowance({
                      ...newAllowance,
                      percentage: e.target.value,
                    })
                  }
                  placeholder="0"
                />
              </div>

              <div style={formStyles.formGroup}>
                <label style={formStyles.label}>
                  {t("baseAmount") || "Base Amount"}
                </label>
                <input
                  type="number"
                  style={formStyles.input}
                  value={newAllowance.baseAmount}
                  onChange={(e) =>
                    setNewAllowance({
                      ...newAllowance,
                      baseAmount: e.target.value,
                    })
                  }
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Toggles */}
            <div
              style={{
                display: "flex",
                gap: "32px",
                flexWrap: "wrap",
                marginBottom: "20px",
              }}
            >
              <Toggle
                checked={!!newAllowance.isTaxable}
                onChange={(v) =>
                  setNewAllowance({ ...newAllowance, isTaxable: v })
                }
                label={t("isTaxable") || "Taxable"}
              />
              <Toggle
                checked={!!newAllowance.isRecurring}
                onChange={(v) =>
                  setNewAllowance({ ...newAllowance, isRecurring: v })
                }
                label={t("isRecurring") || "Recurring (monthly)"}
              />
            </div>

            {/* Description */}
            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t("description")}</label>
              <textarea
                style={{
                  ...formStyles.input,
                  minHeight: "80px",
                  resize: "vertical",
                }}
                value={newAllowance.description}
                onChange={(e) =>
                  setNewAllowance({
                    ...newAllowance,
                    description: e.target.value,
                  })
                }
                placeholder={t("descriptionPlaceholder")}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "20px",
              }}
            >
              <Mo3tiButton
                variant="secondary"
                icon={Save}
                onClick={handleAddAllowance}
                disabled={loading}
              >
                {loading
                  ? t("saving")
                  : isEditing
                  ? t("update") || "Update"
                  : t("saveAllowance") || "Save Allowance"}
              </Mo3tiButton>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AddAllowanceForm;